import React, { useState, useEffect } from 'react';
import { X, User, GraduationCap, Phone, Calendar, CreditCard, Save } from 'lucide-react';
import { Resident } from '../types';
import { NotificationModal } from './NotificationModal';

interface EditResidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  resident: Resident | null;
  onUpdateResident: (residentId: string, updates: Partial<Resident>) => Promise<boolean>;
}

export function EditResidentModal({ isOpen, onClose, resident, onUpdateResident }: EditResidentModalProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [study, setStudy] = useState('');
  const [startDate, setStartDate] = useState('');
  const [motorcyclePlate, setMotorcyclePlate] = useState('');
  const [paymentPattern, setPaymentPattern] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{
    visible: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
  }>({ visible: false, type: 'success', title: '', message: '' });

  // Initialize form with resident data
  useEffect(() => {
    if (resident) {
      setName(resident.name || '');
      setPhone(resident.phone || '');
      setStudy(resident.study || '');
      setStartDate(resident.startDate || '');
      setMotorcyclePlate(resident.motorcyclePlate || '');
      setPaymentPattern(resident.paymentPattern || 1);
    }
  }, [resident]);

  const resetForm = () => {
    setName('');
    setPhone('');
    setStudy('');
    setStartDate('');
    setMotorcyclePlate('');
    setPaymentPattern(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resident || !name.trim()) return;

    setIsSubmitting(true);
    try {
      const success = await onUpdateResident(resident.id, {
        name: name.trim(),
        phone: phone.trim(),
        study: study.trim(),
        startDate,
        motorcyclePlate: motorcyclePlate.trim(),
        paymentPattern
      });

      if (success) {
        setNotification({
          visible: true,
          type: 'success',
          title: 'Berhasil!',
          message: `Data penghuni ${name.trim()} berhasil diperbarui.`
        });
      } else {
        setNotification({
          visible: true,
          type: 'error',
          title: 'Gagal Menyimpan',
          message: 'Gagal memperbarui data penghuni. Silakan coba lagi.'
        });
      }
    } catch (error) {
      console.error('Error updating resident:', error);
      setNotification({
        visible: true,
        type: 'error',
        title: 'Terjadi Kesalahan',
        message: 'Terjadi kesalahan saat memperbarui data. Silakan coba lagi.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, visible: false });
    if (notification.type === 'success') {
      onClose();
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value && !value.startsWith('+62')) {
      value = '+62' + value.replace(/^\\+?62?/, '');
    }
    setPhone(value);
  };

  if (!isOpen || !resident) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Edit Penghuni - Kamar {resident.roomNumber} (Penghuni {resident.position})
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <User className="inline h-4 w-4 mr-1" />
                Nama Lengkap *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Masukkan nama lengkap"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Phone className="inline h-4 w-4 mr-1" />
                Nomor HP
              </label>
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="+62812345678"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Motorcycle Plate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                🏍️ Nomor Plat Motor
              </label>
              <input
                type="text"
                value={motorcyclePlate}
                onChange={(e) => setMotorcyclePlate(e.target.value.toUpperCase())}
                placeholder="BA XXXX"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={25}
              />
            </div>
          </div>

          {/* Study Program */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <GraduationCap className="inline h-4 w-4 mr-1" />
              Program Studi dan Tahun Angkatan
            </label>
            <input
              type="text"
              value={study}
              onChange={(e) => setStudy(e.target.value)}
              placeholder="Contoh: Teknik Informatika 2023"
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Date and Payment Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                Tanggal Masuk *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Payment Pattern */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <CreditCard className="inline h-4 w-4 mr-1" />
                Pola Pembayaran *
              </label>
              <select
                value={paymentPattern}
                onChange={(e) => setPaymentPattern(parseInt(e.target.value))}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(months => (
                  <option key={months} value={months}>
                    Per {months} bulan
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Current Payment Periods Info */}
          {resident.paymentPeriods && resident.paymentPeriods.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-lg border border-yellow-200 dark:border-yellow-700">
              <h3 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                ⚠️ Perhatian: Perubahan Pola Pembayaran
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Penghuni ini memiliki {resident.paymentPeriods.length} periode pembayaran. 
                Mengubah pola pembayaran akan mempengaruhi perhitungan jatuh tempo selanjutnya.
              </p>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t dark:border-gray-700">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim() || !startDate}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Menyimpan...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Simpan Perubahan
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>

      {/* Notification Modal - Outside main container for proper z-index */}
      <NotificationModal
        visible={notification.visible}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={handleNotificationClose}
      />
    </>
  );
}